import { useState } from 'react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { NextQueryParamProvider } from 'next-query-params';
import { ApolloProvider } from '@apollo/client';
import { Auth0Provider } from '@auth0/nextjs-auth0/client';
import { appWithTranslation } from 'next-i18next';

import nextI18NextConfig from '../next-i18next.config.js';
import { EnvContextProvider, getEnv } from 'libs/contexts/env';
import { QuerySearchContextProvider } from 'libs/contexts/querySearch';
import { createApolloClient } from 'libs/utils/apollo-client';

import 'styles/reset.scss';
import 'styles/globals.scss';
import { useFlushStoreCookie } from 'libs/hooks/useFlushStoreCookie';

const ApolloWrapper = ({ children, accessToken }) => {
  const [apolloClient] = useState(createApolloClient(accessToken));

  useFlushStoreCookie(apolloClient);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

function MyApp({ Component, router, pageProps }: AppProps) {
  return (
    <>
      <Head>
        <link rel="icon" href="/public/discovery/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/public/discovery/favicon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/public/discovery/apple-touch-icon.png" />
        <link rel="manifest" href="/public/discovery/manifest.webmanifest" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      <header>
        <div id="BrightSpotHeader" data-sothebys-component-url={getEnv('HEADER_PATH')} />
      </header>

      <main>
        <Auth0Provider profileRoute="/api/auctions/auth/me">
          <EnvContextProvider>
            <NextQueryParamProvider>
              <ApolloWrapper accessToken={pageProps.accessToken}>
                <QuerySearchContextProvider>
                  <Component {...pageProps} key={router.route} />
                </QuerySearchContextProvider>
              </ApolloWrapper>
            </NextQueryParamProvider>
          </EnvContextProvider>
        </Auth0Provider>
      </main>

      <footer>
        <div id="BrightSpotFooter" data-sothebys-component-url={getEnv('FOOTER_PATH')} />
      </footer>
    </>
  );
}

export default appWithTranslation(MyApp, nextI18NextConfig);
